/** HLS + progressive stream attachment for the global radio player. */

import { cspSafeMediaUrl } from './cspMediaUrl';
import { getStoredAuthToken } from './s3MediaAuth';
import { buildGalleryMediaPlaybackUrls } from './studioMediaApi';
import { isStreamBlocked } from './apiFetchPolicy';
import {
  classifyPlaybackFailure,
  isHlsStreamUrl,
  radioStreamProxyUrls,
  resolveRadioPlaybackUrl,
  waitForAudioCanPlay,
} from './radioStreamGuard';

let hlsInstance = null;

function apiBase() {
  return (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
}

function proxiedStreamUrl(rawUrl) {
  if (isStreamBlocked('radio-stream')) return '';
  return `${apiBase()}/radio-stream?url=${encodeURIComponent(rawUrl)}`;
}

export function destroyStreamPlayback() {
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
}

async function attachHlsViaProxy(audioEl, originalUrl) {
  const Hls = (await import('hls.js')).default;
  if (!Hls.isSupported()) {
    throw new Error('HLS streams are not supported in this browser.');
  }

  const base = apiBase();
  hlsInstance = new Hls({
    enableWorker: true,
    lowLatencyMode: false,
    xhrSetup(xhr, url) {
      if (isStreamBlocked('radio-stream')) {
        xhr.abort();
        return;
      }
      const absolute = /^https?:\/\//i.test(url) ? url : new URL(url, originalUrl).href;
      xhr.open('GET', `${base}/radio-stream?url=${encodeURIComponent(absolute)}`, true);
    },
  });
  hlsInstance.loadSource(proxiedStreamUrl(originalUrl));
  hlsInstance.attachMedia(audioEl);

  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('HLS connection timed out.')), 20000);
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
      clearTimeout(timer);
      resolve();
    });
    hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
      if (data?.fatal) {
        clearTimeout(timer);
        reject(new Error(data.type || 'HLS playback failed'));
      }
    });
  });
  await waitForAudioCanPlay(audioEl, 20000);
}

/**
 * Attach a stream URL to an HTMLAudioElement (handles HLS via hls.js when needed).
 * @param {HTMLAudioElement} audioEl
 * @param {string} url — original stream URL
 * @param {{ station?: object }} [opts]
 * @returns {Promise<void>}
 */
export async function attachStreamToAudio(audioEl, url, opts = {}) {
  if (!audioEl || !url) throw new Error('Audio element and URL required');

  const station = opts.station;

  destroyStreamPlayback();
  audioEl.pause();
  audioEl.removeAttribute('src');
  audioEl.load();

  const originalUrl = cspSafeMediaUrl(url);
  if (!originalUrl) throw new Error('Stream URL is not allowed by site security policy.');

  const isRadio = station?.kind === 'radio' || !station?.kind;

  if (isHlsStreamUrl(originalUrl)) {
    const nativeHls =
      audioEl.canPlayType('application/vnd.apple.mpegurl') ||
      audioEl.canPlayType('application/x-mpegURL');
    // Proxied manifests break native HLS segment resolution; hls.js xhrSetup re-proxies each part.
    if (isRadio || !nativeHls) {
      await attachHlsViaProxy(audioEl, originalUrl);
      return;
    }
  }

  const streamUrl = resolveRadioPlaybackUrl({ ...station, url: originalUrl });
  let candidates;
  if (isRadio) {
    candidates = [...new Set([streamUrl, ...radioStreamProxyUrls(originalUrl)].filter(Boolean))].slice(0, 2);
  } else {
    const audioKey = station?.audioKey || opts.s3Key;
    const token = opts.token || getStoredAuthToken();
    if (audioKey && token) {
      const fromKey = await buildGalleryMediaPlaybackUrls(audioKey, token);
      candidates = [...new Set([...fromKey, streamUrl, originalUrl].filter(Boolean))];
    } else {
      candidates = [...new Set([streamUrl, originalUrl].filter(Boolean))];
    }
  }

  let lastErr;
  for (const candidate of candidates) {
    audioEl.src = candidate;
    audioEl.load();
    try {
      await waitForAudioCanPlay(audioEl, isRadio ? 20000 : 30000);
      return;
    } catch (err) {
      lastErr = err;
      audioEl.pause();
      audioEl.removeAttribute('src');
      audioEl.load();
    }
  }
  throw lastErr || new Error('Stream unavailable (proxy offline or host blocked).');
}

export { classifyPlaybackFailure };

export function updateMediaSession(station, { onPlay, onPause, onStop }) {
  if (!('mediaSession' in navigator)) return;

  const title = station?.name || 'Studio Radio';
  const artist =
    station?.kind === 'track'
      ? [station?.artist, station?.album].filter(Boolean).join(' · ') || 'Collection'
      : [station?.country, station?.tags?.split(',')[0]].filter(Boolean).join(' · ') || 'Live radio';
  const album = station?.kind === 'track' ? station?.album || 'Record Collection' : 'Studio Gallery';

  navigator.mediaSession.metadata = new MediaMetadata({
    title,
    artist,
    album,
  });

  navigator.mediaSession.setActionHandler('play', () => onPlay?.());
  navigator.mediaSession.setActionHandler('pause', () => onPause?.());
  navigator.mediaSession.setActionHandler('stop', () => onStop?.());
}

export function setMediaSessionPlaybackState(playing) {
  if ('mediaSession' in navigator) {
    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
  }
}
